/* eslint-disable max-len */
import React, { useState, useRef } from 'react';
import { Toast, Dialog } from '@leverege/ui-elements';
import { GlobalState } from '@leverege/ui-redux';
import P from '@leverege/promise';
import { Plugins, Context, Config } from '@leverege/plugin';
import Path from '@leverege/path';
import I18N from '@leverege/i18n';
import LoadingView from "../../views/LoadingView.js";
import ParentUtil from "../attributes/parent/ParentUtil.js";
import S from './BulkCreateItem.css';
import { BulkBlueprintFormFactory, BulkBlueprintResultsFactory } from "../forms/BulkFactory.js";
import { jsx as _jsx } from "react/jsx-runtime";
const {
  t
} = I18N.ns('molten.imagine.view.BulkCreateItem');
class LoadingMessage extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      message: props.message
    };
  }

  // eslint-disable-next-line react/no-unused-class-component-methods
  setMessage(message) {
    this.setState({
      message
    });
  }
  render() {
    const {
      message
    } = this.state;
    return message;
  }
}
export default function BulkCreateItem(props) {
  const {
    show,
    onClose,
    relationship,
    initialData = {},
    getAttributes,
    tableClassName,
    tableBodyErrorClassName,
    tableBodySuccessClassName
  } = props;
  const {
    blueprint,
    path
  } = relationship;
  const [results, setResults] = useState();
  const loadingMessageRef = useRef();
  async function onSubmit({
    data: props,
    value
  }) {
    const {
      delegate,
      relationship: {
        blueprint
      }
    } = props;
    try {
      if (!Array.isArray(value)) {
        throw new Error('Value should be an array');
      }
      let complete = 0,
        total = value.length;
      const close = LoadingView.showDialog(/*#__PURE__*/_jsx(LoadingMessage, {
        ref: loadingMessageRef,
        message: t('loadingDialog', {
          blueprintName: blueprint.namePlural,
          complete,
          total
        })
      }), 'bulkCreateItem');
      const plgs = Plugins.get('BulkCreateHook', m => {
        return Context.isMatch({
          objectType: blueprint.type,
          path
        }, m);
      });
      let mkDevs = [...value];
      for (let n = 0; n < plgs.length; n++) {
        // eslint-disable-next-line no-await-in-loop
        mkDevs = await plgs[n].hook(mkDevs, props);
      }
      total = mkDevs.length;
      const results = await P.mapLimit(mkDevs, async (dev, index) => {
        const {
          parentRelationships,
          ...data
        } = dev;
        try {
          if (dev.constructor.name === 'Error') throw dev;
          const pRels = await populateParentRelationships(parentRelationships, props);
          const newDevice = await GlobalState.dispatch(delegate.create(data));
          await createParentRelationships(newDevice, pRels, props);
          return {
            success: true,
            original: data,
            data: newDevice,
            type: blueprint.type,
            index
          };
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error);
          return {
            error,
            original: data,
            index
          };
        } finally {
          complete++;
          loadingMessageRef?.current?.setMessage(t('loadingDialog', {
            blueprintName: blueprint.namePlural,
            complete,
            total
          }));
        }
      }, {
        limit: 5
      });
      setResults(results);
      close();
      if (results.filter(d => d.success).length === 0) {
        Toast.error([t('error', {
          blueprintType: blueprint.type
        })]);
      } else if (results.filter(d => !d.success).length === 0) {
        Toast.success(t('fullSuccess', {
          blueprintType: blueprint.type
        }));
      } else {
        Toast.success(t('someSuccess', {
          blueprintType: blueprint.type
        }));
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      Toast.error([t('error', {
        blueprintType: blueprint.type
      }), t('errorBody')]);
    }
  }
  const CreateComponent = BulkBlueprintFormFactory.create(`${blueprint.type}.bulkCreate`, {
    target: {
      type: blueprint.type,
      data: {
        ...initialData
      }
    },
    onCancel: onClose,
    onSubmit,
    contentVariant: Config.get('BulkUpload', 'contentVariant'),
    contentHeaderVariant: Config.get('BulkUpload', 'contentHeaderVariant'),
    contentAreaVariant: Config.get('BulkUpload', 'contentAreaVariant'),
    contentFooterVariant: Config.get('BulkUpload', 'contentFooterVariant'),
    title: Config.get('BulkUpload', 'title', t('bulkTitle', {
      blueprintName: blueprint.name
    })),
    titleIcon: Config.get('BulkUpload', 'titleIcon', blueprint.getIcon('createDialogIcon') || blueprint.getIcon('screenIcon')),
    subtitleVariant: Config.get('BulkUpload', 'subtitleVariant'),
    bodyVariant: Config.get('BulkUpload', 'bodyVariant'),
    downloadVariant: Config.get('BulkUpload', 'downloadVariant'),
    downloadIcon: Config.get('BulkUpload', 'downloadVariant', blueprint.getIcon('bulkCreateDownloadIcon')),
    submitText: t('submitText', {
      blueprintName: blueprint.name
    }),
    eventData: props,
    relationship,
    getAttributes
  });
  const ResultsComponent = BulkBlueprintResultsFactory.create(`${blueprint.type}.bulkCreate`, {
    target: {
      type: blueprint.type,
      data: {
        ...initialData
      }
    },
    onCancel: onClose,
    onSubmit,
    title: t('title', {
      blueprintName: blueprint.namePlural
    }),
    titleIcon: blueprint.getIcon('createDialogIcon') || blueprint.getIcon('screenIcon'),
    submitText: t('submitText', {
      blueprintName: blueprint.namePlural
    }),
    eventData: props,
    relationship,
    getAttributes,
    settingsPath: `${blueprint.type}.bulkCreate`,
    results,
    tableClassName,
    tableBodyErrorClassName,
    tableBodySuccessClassName
  });
  return /*#__PURE__*/_jsx(Dialog, {
    variant: results ? 'dialogFullPage' : 'dialog',
    className: results ? S.dialogFullPage : S.dialog,
    show: show,
    closeOnEscape: false,
    closeOnDocumentClick: false,
    onClose: onClose,
    children: results ? ResultsComponent : CreateComponent
  });
}
async function populateParentRelationships(parentRelationships, props) {
  if (!parentRelationships) {
    return null;
  }
  const {
    relationship,
    delegate
  } = props;
  const {
    objectType
  } = relationship;
  const objRef = {
    type: objectType,
    ref: delegate.getInterfaceUrl()
  };
  const nPRels = {};
  await P.mapLimit(Object.entries(parentRelationships), async ([key, pRel]) => {
    const {
      attribute,
      value
    } = pRel;
    const parentAction = ParentUtil.getParentAction(objRef, attribute);
    const attrObj = attribute.blueprint?.attribute;
    const tags = attrObj.getTagsStartingWith('bulkCreate:network:', true);
    let parentDevice;
    for (let i = 0; i < tags.length; i++) {
      const tag = tags[i];
      const [networkId, aliasKey] = tag.split(':');
      try {
        // eslint-disable-next-line no-await-in-loop
        parentDevice = await GlobalState.dispatch(parentAction.getDeviceByNetworkAlias(networkId, value, aliasKey));
      } catch (err) {
        if (err.status !== 404) {
          throw err;
        }
        throw new Error(`Invalid parent device ${value} for attribute ${attribute.displayName}`);
      }
      if (parentDevice) {
        break;
      }
    }
    if (!parentDevice) {
      throw new Error(`No parent device ${pRel.value} for attribute ${attribute.displayName}`);
    }
    nPRels[key] = {
      ...pRel,
      parentDevice,
      parentAction
    };
  });
  return nPRels;
}
async function createParentRelationships(device, parentRelationships, props) {
  if (!parentRelationships) {
    return null;
  }
  const {
    relationship,
    delegate
  } = props;
  const {
    objectType
  } = relationship;
  const objRef = {
    id: device.id,
    type: objectType,
    ref: delegate.getInterfaceUrl(),
    data: device.data
  };
  return P.mapLimit(Object.entries(parentRelationships), async ([key, pRel]) => {
    const {
      attribute,
      parentDevice,
      parentAction
    } = pRel;
    const parentRef = {
      id: parentDevice.id,
      ref: parentAction.getInterfaceUrl(),
      type: parentAction.relationship.objectType
    };
    return attribute.set(objRef, {
      type: 'add',
      parentRef
    }).then(() => {
      Path.set(key, device, parentDevice.name);
    });
  }, {
    limit: 2
  });
}